import React, { useEffect, useState } from 'react';
import { Accordion, Alert, Badge, Button, Card, Col, Form, InputGroup, Row, Spinner } from 'react-bootstrap';

import {
  useGetSettingsSmtp,
  usePatchSettingsSmtp,
  usePostSettingsSmtpTestConnection,
  getSettingsSmtpSecret,
} from '../../api/generated/arrayscapeGamingDashboardAPI';
import type { SMTPSettings, JsonPatchOperation, WebFormTestResult } from '../../api/generated/model';

// The dashboard's single outbound relay. Every feature that sends
// transactional email uses this, so it lives here rather than inside any one
// feature's settings.
const empty: SMTPSettings = {
  host: '',
  port: 587,
  username: '',
  ehlo_hostname: '',
};

const SMTPTab: React.FC = () => {
  const { data, isLoading, refetch } = useGetSettingsSmtp();
  const patchMutation = usePatchSettingsSmtp();
  const testMutation = usePostSettingsSmtpTestConnection();

  const [form, setForm] = useState<SMTPSettings>(empty);
  const [original, setOriginal] = useState<SMTPSettings>(empty);
  const [password, setPassword] = useState('');
  // What the server reports as stored, kept apart from the pending edit so
  // revealing a value does not count as a change.
  const [revealed, setRevealed] = useState<string | null>(null);
  const [shown, setShown] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testFrom, setTestFrom] = useState('');
  const [testTo, setTestTo] = useState('');
  const [testResult, setTestResult] = useState<WebFormTestResult | null>(null);
  const [msg, setMsg] = useState<{ variant: string; text: string } | null>(null);

  useEffect(() => {
    if (data) {
      setForm(data);
      setOriginal(data);
    }
  }, [data]);

  const passwordDirty = password !== '' && password !== revealed;
  const hasChanges = JSON.stringify(form) !== JSON.stringify(original) || passwordDirty;
  const set = (k: keyof SMTPSettings, v: any) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const patches: JsonPatchOperation[] = (Object.keys(form) as (keyof SMTPSettings)[])
        // Server-computed fields; patching them back is meaningless.
        .filter(k => !['password', 'password_set', 'connection_status', 'connection_message', 'last_test_at'].includes(String(k)))
        .map(k => ({ op: 'add', path: `/${k}`, value: form[k] }));

      if (passwordDirty) {
        patches.push({ op: 'add', path: '/password', value: password });
      }

      await patchMutation.mutateAsync({ data: patches });
      await refetch();
      setPassword('');
      setRevealed(null);
      setShown(false);
      setMsg({ variant: 'success', text: 'Settings saved.' });
    } catch (err: any) {
      setMsg({
        variant: 'danger',
        text: err?.response?.data?.message || err?.response?.data?.error || 'Failed to save settings',
      });
    } finally {
      setSaving(false);
    }
  };

  // Show serves two cases: unmask what was just typed (no request needed), or
  // fetch a stored password that cannot otherwise be seen.
  const toggleShow = async () => {
    if (shown) {
      // Hiding must never destroy typed input, so clear only when what is
      // displayed is the value we fetched.
      if (password !== '' && password === revealed) {
        setPassword('');
        setRevealed(null);
      }
      setShown(false);
      return;
    }

    if (password !== '') {
      setShown(true);
      return;
    }

    setRevealing(true);
    setMsg(null);
    try {
      const res = await getSettingsSmtpSecret();
      setRevealed(res.value);
      setPassword(res.value);
      setShown(true);
    } catch (err: any) {
      setMsg({
        variant: 'danger',
        text: err?.response?.data?.message || err?.response?.data?.error || 'Could not reveal the password',
      });
    } finally {
      setRevealing(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await testMutation.mutateAsync({ data: { from: testFrom, to: testTo } });
      setTestResult(res);
      await refetch();
    } catch (err: any) {
      setTestResult({
        status: 'error',
        message: err?.response?.data?.message || err?.response?.data?.error || 'Could not run the test',
      });
    } finally {
      setTesting(false);
    }
  };

  const textField = (label: string, k: keyof SMTPSettings, help?: string) => (
    <Form.Group className="mb-3">
      <Form.Label>{label}</Form.Label>
      <Form.Control type="text" value={String(form[k] ?? '')} onChange={e => set(k, e.target.value)} />
      {help && <Form.Text muted>{help}</Form.Text>}
    </Form.Group>
  );

  if (isLoading) return <Spinner animation="border" />;

  const statusBadge = () => {
    switch (form.connection_status) {
      case 'connected': return <Badge bg="success">last test passed</Badge>;
      case 'error': return <Badge bg="danger">last test failed</Badge>;
      default: return <Badge bg="secondary">not tested</Badge>;
    }
  };

  return (
    <Card>
      <Card.Body>
        {msg && <Alert variant={msg.variant} dismissible onClose={() => setMsg(null)}>{msg.text}</Alert>}

        <p className="text-muted small">
          The dashboard's outbound mail relay. Every feature that sends transactional
          email — website form notifications, alerts — goes through this one relay.
          Marketing email is separate and lives on the SendGrid tab.
          {' '}The relay has no From address of its own: each feature sets the identity
          it sends as, so web-form notifications and future alerts can differ.
        </p>

        <h6 className="text-muted">
          Relay{' '}
          {form.username
            ? <Badge bg="info">using SMTP AUTH</Badge>
            : <Badge bg="success">using IP allowlisting</Badge>}
          {' '}{statusBadge()}
        </h6>
        {form.connection_message && (
          <p className={`small ${form.connection_status === 'error' ? 'text-danger' : 'text-muted'}`}>
            {form.connection_message}
            {form.last_test_at && <span className="text-muted"> ({new Date(form.last_test_at).toLocaleString()})</span>}
          </p>
        )}
        <p className="text-muted small mb-2">
          Which auth method is in use is decided by the Username field: blank means the
          relay is trusting this server's IP address, and no credential is stored
          anywhere. Filling it in switches to SMTP AUTH.
        </p>

        <Row>
          <Col md={5}>{textField('Host', 'host', 'GCP blocks outbound port 25 permanently, so mail must relay.')}</Col>
          <Col md={2}>
            <Form.Group className="mb-3">
              <Form.Label>Port</Form.Label>
              <Form.Control
                type="number"
                value={Number(form.port ?? 0)}
                onChange={e => set('port', parseInt(e.target.value || '0', 10))}
              />
              <Form.Text muted>587 (STARTTLS).</Form.Text>
            </Form.Group>
          </Col>
          <Col md={5}>{textField('Username', 'username', 'Leave blank for IP allowlisting. A full Workspace address switches to SMTP AUTH.')}</Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Password{' '}
                {form.password_set
                  ? <Badge bg="success">configured</Badge>
                  : <Badge bg="secondary">not set</Badge>}
              </Form.Label>
              <InputGroup>
                <Form.Control
                  type={shown ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder={form.password_set ? 'Leave blank to keep the stored value' : ''}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
                <Button
                  variant="outline-secondary"
                  disabled={(!form.password_set && password === '') || revealing}
                  onClick={toggleShow}
                  title={shown ? 'Hide' : password !== '' ? 'Show what you typed' : 'Show the stored value'}
                >
                  {revealing
                    ? <Spinner animation="border" size="sm" />
                    : <i className={`bi ${shown ? 'bi-eye-slash' : 'bi-eye'}`}></i>}
                </Button>
              </InputGroup>
              <Form.Text muted>App password. Only needed when a username is set.</Form.Text>
            </Form.Group>
          </Col>
          <Col md={6}>{textField('EHLO hostname', 'ehlo_hostname', "What this client announces in EHLO. Blank derives it from each message's From domain, which is normally what you want.")}</Col>
        </Row>

        <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
          <Form.Control
            type="email"
            size="sm"
            style={{ maxWidth: 240 }}
            placeholder="send as…"
            value={testFrom}
            onChange={e => setTestFrom(e.target.value)}
          />
          <Form.Control
            type="email"
            size="sm"
            style={{ maxWidth: 240 }}
            placeholder="send to…"
            value={testTo}
            onChange={e => setTestTo(e.target.value)}
          />
          <Button
            variant="outline-primary"
            size="sm"
            onClick={runTest}
            disabled={testing || hasChanges || testFrom.trim() === '' || testTo.trim() === ''}
          >
            {testing
              ? <><Spinner animation="border" size="sm" className="me-1" />Testing…</>
              : <><i className="bi bi-plug me-1"></i>Test</>}
          </Button>
          <span className="text-muted small">
            {hasChanges
              ? 'Save your changes first — the test runs against saved settings.'
              : 'Sends a real email. The relay has no sender of its own, so name one.'}
          </span>
        </div>

        {testResult && (
          <Alert
            variant={testResult.status === 'ok' ? 'success' : 'danger'}
            dismissible
            onClose={() => setTestResult(null)}
          >
            <div className="fw-semibold">{testResult.message}</div>
            {testResult.stages && testResult.stages.length > 0 && (
              <ul className="mb-0 mt-2 small list-unstyled">
                {testResult.stages.map((st, i) => (
                  <li key={i}>
                    <i className={`bi ${st.ok ? 'bi-check-circle text-success' : 'bi-x-circle text-danger'} me-2`}></i>
                    {st.name}
                    {st.detail && <span className="text-muted"> — {st.detail}</span>}
                  </li>
                ))}
              </ul>
            )}
          </Alert>
        )}

        <Accordion className="mb-3">
          <Accordion.Item eventKey="ip">
            <Accordion.Header>
              <i className="bi bi-shield-lock me-2"></i>
              Set up IP allowlisting (recommended — stores no credential)
            </Accordion.Header>
            <Accordion.Body className="small">
              <p>
                The relay trusts this server by its public IP address, so there is no
                password to store, rotate, or leak. This is the preferred method.
              </p>
              <ol className="ps-3">
                <li className="mb-2">
                  <strong>Give this server a static external IP.</strong> On GCP an
                  ephemeral address changes whenever the VM stops and starts, which
                  would silently break relay. Reserve a static external IP and assign
                  it to the VM before continuing.
                </li>
                <li className="mb-2">
                  In the Google Admin console, go to{' '}
                  <strong>Apps → Google Workspace → Gmail → Routing</strong>, find{' '}
                  <strong>SMTP relay service</strong>, and add or edit an entry.
                </li>
                <li className="mb-2">
                  <strong>Allowed senders:</strong> "Only addresses in my domains".
                  This is what makes the From address acceptable to the relay — it
                  must be on a domain you own. Choosing "Only registered Apps users"
                  instead requires the From address to be a real account or alias.
                </li>
                <li className="mb-2">
                  <strong>Authentication:</strong> tick{' '}
                  <em>Only accept mail from the specified IP addresses</em> and add
                  the static IP from step 1. Leave{' '}
                  <em>Require SMTP Authentication</em> unticked — ticking it forces
                  the username/password path instead.
                </li>
                <li className="mb-2">
                  <strong>Encryption:</strong> tick <em>Require TLS encryption</em>.
                  This service always issues STARTTLS on port 587 before sending
                  anything, and aborts if the relay does not offer it.
                </li>
                <li>
                  Leave Username and Password blank here. If the test then fails at
                  the sender stage, the error names the address the relay actually
                  saw — allowlist that exact value, and note it may be IPv6 even when
                  an IPv4 address was registered.
                </li>
              </ol>
            </Accordion.Body>
          </Accordion.Item>

          <Accordion.Item eventKey="auth">
            <Accordion.Header>
              <i className="bi bi-key me-2"></i>
              Set up SMTP AUTH with an app password (fallback)
            </Accordion.Header>
            <Accordion.Body className="small">
              <p>
                Use this only when IP allowlisting is unavailable — for example if
                this server has no static address. It stores a credential, so it is
                the weaker option.
              </p>
              <ol className="ps-3">
                <li className="mb-2">
                  Pick a Workspace account to send as. It must have{' '}
                  <strong>2-Step Verification enabled</strong> — app passwords cannot
                  be generated without it.
                </li>
                <li className="mb-2">
                  Signed in as that account, go to{' '}
                  <code>myaccount.google.com/apppasswords</code> and generate a new
                  app password. Google shows the 16-character value once and never
                  again, so paste it straight into the Password field.
                </li>
                <li className="mb-2">
                  In the Admin console under{' '}
                  <strong>Apps → Google Workspace → Gmail → Routing → SMTP relay
                  service</strong>, tick <em>Require SMTP Authentication</em> and{' '}
                  <em>Require TLS encryption</em>.
                </li>
                <li className="mb-2">
                  Set Username to that account's full email address (not just the
                  local part), and Password to the app password — not the account's
                  normal password, which the relay will reject.
                </li>
                <li>
                  Revoking the app password immediately breaks delivery. Web form
                  submissions still persist and can be resent from the Form
                  Submissions page once a new one is set.
                </li>
              </ol>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>

        <div className="d-flex justify-content-end pt-2 border-top">
          <Button
            variant="outline-secondary"
            className="me-2"
            disabled={!hasChanges || saving}
            onClick={() => { setForm(original); setPassword(''); setRevealed(null); setShown(false); }}
          >
            Cancel
          </Button>
          <Button variant="primary" disabled={!hasChanges || saving} onClick={save}>
            {saving ? <><Spinner animation="border" size="sm" className="me-1" />Saving…</> : 'Save'}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

export default SMTPTab;
